/**
 * Template Registry — single source of truth for starter site templates.
 *
 * Composed from two parts:
 *   - `templates.base.ts`     — hand-authored static fields + schemas (SSOT)
 *   - `themes.generated.ts`   — per-template `theme` summary, generated from each
 *                               template's theme.css + font-manifest.json by
 *                               `scripts/extract-theme.ts` (never hand-edited)
 *
 * Consumed by:
 *   - react-app onboarding + site-editor pickers (name / tagline / theme / previewUrl)
 *   - react-app worker session handlers (id → bundleKey for getPullUrl)
 *   - contracts (TemplateId payload schema for bootstrapWorkspace / setHeroStyle)
 *   - bundle-template.sh / deploy-template-previews.sh (via scripts/print.ts)
 *
 * Adding a template = one entry in `templates.base.ts` + its directory under
 * heysegment-templates/, then `./bundle-template.sh <slug>` (auto-extracts theme).
 *
 * @module
 */
import { Schema as S } from "effect"
import {
  TemplateIdSchema,
  ThemeSummarySchema,
  TEMPLATE_BASE,
  TemplateBaseSchema,
  type TemplateId,
  type ThemeSummary,
} from "./templates.base.js"
import { THEME_SUMMARIES } from "./themes.generated.js"

export { TemplateIdSchema, ThemeSummarySchema }
export type { TemplateId, ThemeSummary }

export const TemplateSchema = S.Struct({
  ...TemplateBaseSchema.fields,
  /** Generated by extract-theme.ts at bundle time; never hand-edited. */
  theme: ThemeSummarySchema,
})
export type Template = typeof TemplateSchema.Type

export const TEMPLATES: ReadonlyArray<Template> = TEMPLATE_BASE.map((base) => ({
  ...base,
  theme: THEME_SUMMARIES[base.id],
}))

/** `id → bundleKey` map for server-side pull resolution. */
export const templateBundleKeys = (): Record<string, string> =>
  Object.fromEntries(TEMPLATES.map((t) => [t.id, t.bundleKey]))
